import * as datefsn from "date-fns";
import { formatDate } from "./formatting";
import i18n, { tn } from "../i18n";
import { dateInTimezone } from "./timezone";

export function now() {
  return new Date();
}

export function today() {
  return datefsn.startOfDay(new Date());
}

export function daysAgo(days: number) {
  return datefsn.subDays(today(), days);
}

export function isOverdue(date: Date) {
  const today = new Date();
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  return dateOnly < todayOnly;
}

export function isThisWeek(date: Date) {
  return datefsn.isThisWeek(date);
}

export function isThisQuarter(date: Date) {
  return datefsn.isThisQuarter(date);
}

export function isThisYear(date: Date) {
  return datefsn.isThisYear(date);
}

export function isLastQuarter(date: Date) {
  const today = new Date();
  const lastQuarter = datefsn.subQuarters(today, 1);
  return datefsn.isSameQuarter(date, lastQuarter);
}

export function isLastYear(date: Date) {
  const today = new Date();
  const lastYear = datefsn.subYears(today, 1);
  return datefsn.isSameYear(date, lastYear);
}

export function isFirstDayOfMonth(date: Date) {
  return date.getDate() === 1;
}

export function isLastDayOfMonth(date: Date) {
  return date.getDate() === new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

export function addDays(date: Date, days: number) {
  return datefsn.addDays(date, days);
}

export function isWithinTimeframe(timestamp: string, hours: number): boolean {
  const now = new Date();
  const checkInTime = new Date(timestamp);
  const diffInHours = (now.getTime() - checkInTime.getTime()) / (1000 * 60 * 60);
  return diffInHours <= hours;
}

export function daysFromNow(days: number) {
  return datefsn.addDays(today(), days);
}

export function compareAsc(date1: Date | null, date2: Date | null, { nullsFirst = false } = {}) {
  if (!date1 && !date2) return 0;
  if (!date1 && date2) return nullsFirst ? -1 : 1;
  if (date1 && !date2) return nullsFirst ? 1 : -1;

  return datefsn.compareAsc(date1!, date2!);
}

export function compareDesc(date1: Date | null, date2: Date | null, { nullsFirst = false } = {}) {
  if (!date1 && !date2) return 0;
  if (!date1 && date2) return nullsFirst ? -1 : 1;
  if (date1 && !date2) return nullsFirst ? 1 : -1;

  return datefsn.compareDesc(date1!, date2!);
}

export function parse(date: string | Date | null | undefined) {
  if (date === null || date === undefined) {
    return null;
  }

  if (date instanceof Date) {
    return date;
  }

  if (typeof date === "string") {
    if (date.length === 10) {
      return parseDate(date);
    } else {
      let res = parseISO(date);
      if (isNaN(res.getTime())) throw new Error("Invalid date");
      return res;
    }
  }

  throw new Error("Invalid date");
}

export function parseDate(date: Date | string | null | undefined): Date | null {
  if (date === null || date === undefined) return null;
  if (date instanceof Date) return date;

  const d = date as string;

  if (d.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return datefsn.parse(date as string, "yyyy-MM-dd", new Date());
  }

  // parse dates like "2024-07-15T00:00:00Z"
  if (d.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/)) {
    const shorted = d.slice(0, 10);
    return datefsn.parse(shorted, "yyyy-MM-dd", new Date());
  }

  throw new Error("Invalid date " + date);
}

export function parseISO(date: string) {
  return datefsn.parseISO(date);
}

export function isToday(date: Date) {
  return datefsn.isToday(date);
}

export function isYesterday(date: Date) {
  return datefsn.isYesterday(date);
}

export function isTomorrow(date: Date) {
  return datefsn.isTomorrow(date);
}

export function isPast(date: Date) {
  return datefsn.isPast(date);
}

export function isFuture(date: Date) {
  return datefsn.isFuture(date);
}

export function toDateWithoutTime(date: Date) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}-${month.toString().padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
}

export function isCurrentYear(date: Date, timezone?: string) {
  const currentDate = now();

  if (timezone) {
    return dateInTimezone(date, timezone).getFullYear() === dateInTimezone(currentDate, timezone).getFullYear();
  }

  return date.getFullYear() === currentDate.getFullYear();
}

export function weeksBetween(start: Date, end: Date) {
  return datefsn.differenceInWeeks(end, start);
}

export function daysBetween(start: Date, end: Date) {
  return datefsn.differenceInDays(end, start);
}

export function hoursBetween(start: Date, end: Date) {
  return datefsn.differenceInHours(end, start);
}

export function getMonthName(date: Date) {
  return formatDate(date, i18n.resolvedLanguage, { month: "long" });
}

export function isSameDay(date1: Date, date2: Date) {
  return datefsn.isSameDay(date1, date2);
}

export function relativeDay(date: Date) {
  const startOfToday = datefsn.startOfDay(new Date());
  const startOfDayOfDate = datefsn.startOfDay(date);

  const days = daysBetween(startOfDayOfDate, startOfToday);

  if (days === 0) return i18n.t("Today");
  if (days === 1) return i18n.t("Yesterday");

  return tn("{{count}} day ago", "{{count}} days ago", days);
}

export function dateChanged(old: Date | null, current: Date | null): boolean {
  if (!old && !current) return false;
  if (!old && current) return true;
  if (old && !current) return true;

  return !isSameDay(old!, current!);
}

export function durationHumanized(a: Date, b: Date, suffix?: string): string {
  const days = daysBetween(a, b);
  if (days === 0) return i18n.t("Last day");
  if (days < 14) return withSuffix(tn("1 day", "{{count}} days", days), suffix);

  const weeks = weeksBetween(a, b);
  if (days < 60) return withSuffix(tn("1 weeks", "{{count}} weeks", weeks), suffix);

  const months = Math.floor(days / 30);
  if (days < 365) return withSuffix(tn("1 months", "{{count}} months", months), suffix);

  const years = Math.floor(days / 365);
  return withSuffix(tn("1 year", "{{count}} years", years), suffix);
}

function withSuffix(str: string, suffix?: string): string {
  if (!suffix) return str;
  return `${str} ${suffix}`;
}

export function startOfCurrentYear() {
  const now = new Date();
  return new Date(now.getFullYear(), 0, 1);
}

export function endOfCurrentYear() {
  const now = new Date();
  return new Date(now.getFullYear(), 11, 31);
}

export function overdueDays(date: Date): number | null {
  const now = +new Date();
  const d = date.getTime();

  if (now < d) return null;

  const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));

  if (days < 1) {
    return null;
  }

  return days;
}
